import {
  DEFAULT_TAB_DETECTION_CONFIG,
  TAB_DETECTION_CONFIG_TOKEN,
  TabDetectionConfig,
} from '@app/tokens/tab-detection-injection-token';
import {
  EnvironmentProviders,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';
import { delay, of } from 'rxjs';

/**
 * Configures and provides shared tab detection functionality application-wide.
 *
 * This function sets up environment providers for detecting multiple tabs of the application
 * running concurrently, using the provided configuration or falling back to default settings.
 * It ensures proper initialization by adding a delay to handle potential race conditions
 * when detecting duplicate tabs, particularly when using the BroadcastChannel API.
 *
 * @param {Partial<TabDetectionConfig>} [config] - An optional partial configuration object
 * that overrides default tab detection settings.
 * @returns {EnvironmentProviders} An object containing the required environment providers
 * for tab detection functionality across the application.
 */
export const provideTabDetectionAppWide = (
  config?: Partial<TabDetectionConfig>
): EnvironmentProviders => {
  return makeEnvironmentProviders([
    {
      provide: TAB_DETECTION_CONFIG_TOKEN,
      useFactory: () => {
        return {
          ...DEFAULT_TAB_DETECTION_CONFIG,
          customMessage: 'TAB_DETECTION.DUPLICATED_TAB', // Just to test override
          ...(config ?? {}),
        };
      },
    },
    // Give the service time to detect duplicates via BroadcastChannel
    // Wait for BroadcastChannel messages (to prevent race condition)
    provideAppInitializer(() => of(null).pipe(delay(1000))),
  ]);
};
